import { Field, FieldArray, Form, Formik } from "formik";
import { FC, Fragment, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  ModalBody,
  ModalFooter,
  Select,
  Text,
  VStack
} from "@chakra-ui/react";
import isValidDomain from "is-valid-domain";
import { ButtonAdd, DividerWithText, Modal, FormInput } from "components";
import { useToastNotification, useTeamInvites, useTeamMembers } from "lib/hooks";
import { CaretForwardIcon } from "lib/icons/caretForward";
import { ManageDomainInvites, ManageSentSingleInvites } from "../invites";
import { TeamInvite, TeamInviteType, TeamPermissions } from "lib/types";
import { teamInvitesClient } from "lib/clients";

type Props = {
  isOpen: boolean;
  onClose: () => void;
}

type State = {
  pendingDomain: string;
  domainInvites: string[];
  pendingDomainInvites: string[];
}

const inviteInitialValue: Pick<TeamInvite, 'match' | 'permissions'> = {
  match: '',
  permissions: TeamPermissions.MEMBER,
};

const initialState: State = {
  pendingDomain: '',
  domainInvites: [],
  pendingDomainInvites: [],
}

export const ManageTeamInvitesModal: FC<Props> = ({ isOpen, onClose }) => {
  const [ invites, setInvites ] = useState<State>(initialState);
  const { errorToast, successToast } = useToastNotification();
  const { mutate: mutateInvites } = useTeamInvites();
  const { data: teamMembers } = useTeamMembers();

  const validateSingleInvite = (value: string): string | void => {
    const isTeamMember = !!teamMembers.find(({ user }) => user.email === value);

    if (isTeamMember) {
      return 'This person is already part of your team'
    }
  }

  const sendInvite = async ({ match, permissions, type }: TeamInvite): Promise<void> => {
    try {
      const { data } = await teamInvitesClient.postInvite({ match: match.trim(), permissions, type });
      await mutateInvites();
      return data;
    } catch (err) {
      let description = 'Could not send invite';
      errorToast({ title: "Error", description });
    }
  }

  const onSubmit = async (values) => {
    let pendingInvites = [];

    // A pending domain is one that a user has entered into the input
    // but not pressed one of the trigger characters to add it as a tag
    if (isValidDomain(invites.pendingDomain) && !values.invitees.includes(invites.pendingDomain)) {
      pendingInvites.push(invites.pendingDomain);
    }

    if (values?.invitees) {
      // We need to clean here, as the form by default will send values of an empty email and permissions
      const clean = values.invitees.filter(({ match }) => match);
      pendingInvites = [ ...pendingInvites, ...clean];
    }

    if (invites.pendingDomainInvites.length) {
      pendingInvites = [ ...pendingInvites, ...invites.pendingDomainInvites ];
    }

    // If there's no pending invites, just close the modal
    if (pendingInvites.length < 1) {
      onClose();
      return;
    }

    const sentInvites = [];

    for (let i = 0; i < pendingInvites.length; i++) {
      let invite = pendingInvites[i];

      const type = typeof invite === 'string' ? TeamInviteType.GROUP : TeamInviteType.INDIVIDUAL;

      if (type === TeamInviteType.GROUP) {
        invite = { match: invite, permissions: TeamPermissions.MEMBER }
      }

      const data = await sendInvite({ ...invite, type })
      sentInvites.push(data);
    }

    await Promise.all(sentInvites)
      .then(() => {
        successToast({
          title: "Success",
          description: "Invitations have been sent",
        })
      })
      .then(() => {
        setInvites(initialState);
        onClose();
      })
      .catch((err) => console.error(err));
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage team invites" size={"lg"}>
      <Formik
        key="invite-single-form"
        initialValues={{ invitees: [ inviteInitialValue ] }}
        onSubmit={(values) => onSubmit(values)}
      >
        {({ values }) => (
          <Form style={{ width: "100%" }}>
            <ModalBody>
              <ManageSentSingleInvites />
              <VStack spacing={15} width="100%" alignItems="start">
                <FieldArray name="invitees">
                  {(arrayHelpers) => (
                    <VStack spacing={3} width="100%" alignItems="start">
                      {values.invitees?.map((_, index) => (
                        <Fragment key={index}>
                          <Box className="team-invite__split">
                            <Field name={`invitees.${index}.match`} validate={validateSingleInvite}>
                              {({ field }) => (
                                <FormInput name={`invitees.${index}.match`} placeholder={"Enter email address"} size="sm" type="email" {...field} />
                              )}
                            </Field>
                            <Field name={`invitees.${index}.permissions`}>
                              {({ field }) => (
                                <FormControl id="permissions">
                                  <Select variant="primary" size="sm" {...field} icon={<CaretForwardIcon fill="brand.500" />}>
                                    <option value={TeamPermissions.MEMBER}>Member</option>
                                    <option value={TeamPermissions.ADMIN}>Admin</option>
                                  </Select>
                                </FormControl>
                              )}
                            </Field>
                          </Box>
                          <Divider display={(values.invitees.length - 1) === index ? 'none' : 'block' } />
                        </Fragment>
                      ))}
                      <ButtonAdd variant="tertiary" onClick={() => arrayHelpers.push(inviteInitialValue)}>
                        Add another
                      </ButtonAdd>
                    </VStack>
                  )}
                </FieldArray>
                <DividerWithText>or</DividerWithText>
                <ManageDomainInvites setInvites={setInvites} invites={invites} />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup width="100%" justifyContent="end">
                <Button size="md" variant="cancel" onClick={onClose}>Cancel</Button>
                <Button type="submit" size="md" variant="submit" data-testid={"send-team-invites"}>Send team invites</Button>
              </ButtonGroup>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  )
};
